import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { combineLatest, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { BookingItem } from '@fry/booking/lib/booking-items';

import { EditorBookingItem } from '../editor-booking-item/editor-booking-item.model';
import { EditorBookingItemRootEditComponent } from '../editor-booking-item-root-edit/editor-booking-item-root-edit.component';
import { EditorBookingItemTree } from './editor-booking-item-tree.model';
import { EditorBookingItemTreeDataEditComponent } from '../editor-booking-item-tree-data-edit/editor-booking-item-tree-data-edit.component';
import { EditorBookingItemTreeStore } from './editor-booking-item-tree.store';
import { EditorBookingItemEditComponent } from '../editor-booking-item-edit/editor-booking-item-edit.component';
import { EditorBookingItemEditStore } from '../editor-booking-item-edit/editor-booking-item-edit.store';
import { EditorDialogStore } from '../editor-dialog/editor-dialog.store';
import { EditorBookingItemBulkNameEditStore } from '../editor-booking-item-bulk-name-edit/editor-booking-item-bulk-name-edit.store';
import { EditorBookingItemBulkNameEditComponent } from '../editor-booking-item-bulk-name-edit/editor-booking-item-bulk-name-edit.component';
import { SecurityService } from '@fry/lib/security';
import { NotifyService } from '@fry/lib/utils';


@Component({
    selector: 'eas-editor-booking-item-tree',
    templateUrl: './editor-booking-item-tree.component.html',
    styleUrls: ['./editor-booking-item-tree.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditorBookingItemTreeComponent implements OnInit, OnDestroy {
  private destroyed$ = new Subject<void>();

  itemTree: EditorBookingItemTree;
  item: EditorBookingItem;
  bookingItem: BookingItem;

  constructor(
    private dialog: MatDialog,
    private itemTreeStore: EditorBookingItemTreeStore,
    private itemEditStore: EditorBookingItemEditStore,
    private itemDialogStore: EditorDialogStore,
    private itemBulkNameEditStore: EditorBookingItemBulkNameEditStore,
    private security: SecurityService,
    private notify: NotifyService,
  ) {

  }

  ngOnInit() {
    this.itemTreeStore.itemTree$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(itemTree => this.itemTree = itemTree);

    this.itemTreeStore.rootItem$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(item => this.item = item);

    this.itemTreeStore.bookingItem$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(bookingItem => this.bookingItem = bookingItem);

    combineLatest(this.itemDialogStore.dialog$, this.itemTreeStore.editingItemTree$).pipe(
      takeUntil(this.destroyed$),
      filter(([dialog, editingItemTree]) => dialog === 'bookingItemTree' && editingItemTree !== null)
    ).subscribe(_ => this.dialog.open(EditorBookingItemTreeDataEditComponent, {
      closeOnNavigation: true,
      disableClose: true,
      hasBackdrop: true,
      minWidth: '50vw',
      id: 'booking-item-editor-form-dialog',
    }));

    combineLatest(this.itemDialogStore.dialog$, this.itemTreeStore.editingItemTree$, this.itemTreeStore.editingItem$).pipe(
      takeUntil(this.destroyed$),
      filter(([dialog, editingItemTree, editingItem]) => dialog === 'bookingItemRoot' && editingItemTree !== null && editingItem !== null)
    ).subscribe(_ => this.dialog.open(EditorBookingItemRootEditComponent, {
      closeOnNavigation: true,
      disableClose: true,
      hasBackdrop: true,
      minWidth: '50vw',
      id: 'booking-item-editor-form-dialog',
    }));

    combineLatest(this.itemDialogStore.dialog$, this.itemTreeStore.editingItem$).pipe(
      takeUntil(this.destroyed$),
      filter(([dialog, editingItem]) => dialog === 'bookingItem' && editingItem !== null)
    ).subscribe(_ => this.dialog.open(EditorBookingItemEditComponent, {
      closeOnNavigation: true,
      disableClose: true,
      hasBackdrop: true,
      minWidth: '50vw',
      id: 'booking-item-editor-form-dialog',
    }));

    this.itemBulkNameEditStore.replacingBulkNameList$.pipe(
      takeUntil(this.destroyed$),
      filter(replacingBulkNameList => replacingBulkNameList !== undefined)
    ).subscribe(_ => this.dialog.open(EditorBookingItemBulkNameEditComponent, {
      closeOnNavigation: true,
      disableClose: true,
      hasBackdrop: true,
      minWidth: '50vw',
      id: 'booking-item-editor-form-dialog',
    }));
  }

  ngOnDestroy() {
    this.itemTreeStore.terminate();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onEdit() {
    this.itemEditStore.startEditing(this.item.id, { name: 'name', value: null });
    this.itemDialogStore.editBookingItem();
    return false;
  }

  onSave() {
    // Check for permission restriction first
    this.security.hasPermission('bookingItems.edit', {roles: this.itemTree.extraRoles}).subscribe(hasPermission => {
      if (!hasPermission) {
        // If permission is granted, show the dialog as normal
        this.notify.warningDialog('Restricted access', 'This booking item has been configured with restricted access roles. Please choose a role which allows you to edit this booking item.')
      } else {
        this.itemTreeStore.save();
      }
    });

    return false; // Prevent default form behavior
  }

}
